import { DATA_STATUS_LABEL, type MetricValue } from '@oxxean/types';
import { formatCoverage, formatNumber, type ValueFormat } from '../lib/format';

/**
 * Presentación de un `MetricValue`. ÚNICO lugar de la aplicación autorizado a
 * convertir un indicador en texto.
 *
 * Cierra el defecto D-04 en la capa visual (DEC-A07, definicion-indicadores.md §6.2):
 *
 * | Estado          | Qué se muestra                                            |
 * |-----------------|-----------------------------------------------------------|
 * | `REAL`          | La cifra                                                  |
 * | `MANUAL`        | La cifra + marca de origen manual                         |
 * | `PARTIAL`       | La cifra + aviso visible de cobertura incompleta          |
 * | `NO_RECORDS`    | «Sin registros». Jamás un 0 % ni un 100 %                 |
 * | `NOT_AVAILABLE` | «No disponible» + el motivo de `note`                     |
 *
 * `MetricValue.value` es `number | null`: si el estado admite cifra pero el valor
 * llega nulo, el componente degrada a «No disponible» en lugar de imprimir algo.
 */

export type MetricSize = 'lg' | 'md' | 'sm';

export interface MetricDisplayProps {
  metric: MetricValue;
  format: ValueFormat;
  size?: MetricSize;
  /** Coloca valor y avisos en una sola línea (celdas de tabla). */
  inline?: boolean;
  /** Oculta el texto del motivo; el `title` del contenedor lo conserva. */
  hideNote?: boolean;
  /** Muestra numerador/denominador: permite auditar el cálculo desde la interfaz. */
  showFraction?: boolean;
}

const VALUE_CLASS: Record<MetricSize, string> = {
  lg: 'metric__value',
  md: 'metric__value metric__value--md',
  sm: 'metric__value metric__value--sm',
};

const ABSENT_CLASS: Record<MetricSize, string> = {
  lg: 'metric__absent metric__absent--lg',
  md: 'metric__absent metric__absent--md',
  sm: 'metric__absent metric__absent--sm',
};

export function MetricDisplay({
  metric,
  format,
  size = 'lg',
  inline = false,
  hideNote = false,
  showFraction = false,
}: MetricDisplayProps): JSX.Element {
  const wrapperClass = `metric${inline ? ' metric--inline' : ''}`;
  const coverage = formatCoverage(metric.coverage);

  // ─── Ausencia de dato: nunca una cifra ────────────────────────────────────
  if (metric.status === 'NO_RECORDS') {
    return (
      <div className={wrapperClass} title={metric.note}>
        <span className={ABSENT_CLASS[size]}>{DATA_STATUS_LABEL.NO_RECORDS}</span>
        {!hideNote && (
          <span className="metric__note">
            {metric.note ??
              (metric.denominator === 0
                ? 'No hubo registros en el período: el indicador no es calculable.'
                : 'No hubo registros en el período.')}
          </span>
        )}
      </div>
    );
  }

  if (metric.status === 'NOT_AVAILABLE' || metric.value === null) {
    const note =
      metric.note ??
      (metric.status === 'NOT_AVAILABLE'
        ? 'La fuente no está integrada o el permiso no la autoriza.'
        : 'El indicador no trae valor para este período.');
    return (
      <div className={wrapperClass} title={note}>
        <span className={ABSENT_CLASS[size]}>{DATA_STATUS_LABEL.NOT_AVAILABLE}</span>
        {!hideNote && <span className="metric__note">{note}</span>}
      </div>
    );
  }

  // ─── Estados con cifra: REAL, PARTIAL, MANUAL ─────────────────────────────
  const formatted = formatNumber(metric.value, format);
  const fraction =
    showFraction &&
    metric.numerator !== undefined &&
    metric.numerator !== null &&
    metric.denominator !== undefined &&
    metric.denominator !== null
      ? `${formatNumber(metric.numerator, 'integer')} de ${formatNumber(metric.denominator, 'integer')}`
      : null;

  return (
    <div className={wrapperClass}>
      <span className={VALUE_CLASS[size]}>
        {formatted}
        {metric.sourceUnit !== undefined && size !== 'sm' && (
          <span className="metric__note" style={{ marginLeft: 6 }}>
            {metric.sourceUnit}
          </span>
        )}
      </span>

      {metric.status === 'PARTIAL' && (
        <span
          className="metric__flag"
          title={
            metric.note ??
            'La sincronización del período está incompleta: la cifra puede variar.'
          }
        >
          {coverage === null ? 'Cobertura incompleta' : `Cobertura ${coverage}`}
        </span>
      )}

      {metric.status === 'MANUAL' && (
        <span
          className="metric__flag metric__flag--manual"
          title={metric.note ?? 'Dato capturado en la plataforma, con autor identificado.'}
        >
          {DATA_STATUS_LABEL.MANUAL}
        </span>
      )}

      {!hideNote && fraction !== null && <span className="metric__note">{fraction}</span>}

      {!hideNote && metric.status === 'PARTIAL' && metric.note !== undefined && (
        <span className="metric__note">{metric.note}</span>
      )}
    </div>
  );
}
